import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';

const style = {
  cursor: 'move',
};

function MovingColumnCards({
  colIdx,
  col,
  handleEditColumn,
  id,
  moveColumn,
  handleDelete,
  handleActiveInactive,
  selectedColIds,
  handleCheckOdColumns,
  disableButtons,
  index,
  callApi,
}) {
  const ref = useRef(null);
  const { t } = useTranslation('common');

  const {
    Users: { userGroupsCardList, logInRights },
    ProductCPReducer: { supplierList },
  } = useSelector((reducer) => reducer);

  const { CheckActionPermission } = useComponent();
  const productAddEditAccess = CheckActionPermission(logInRights, 'criticalPath', 'product', 'addEdit');

  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },

    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = colIdx;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      if (!disableButtons) {
        moveColumn(dragIndex, hoverIndex);
        item.index = hoverIndex;
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { id, colIdx, index };
    },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (item && dropResult) {
        callApi();
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0.5 : 1;
  if (!disableButtons) {
    drag(drop(ref));
  }
  const renderColumnType = () => {
    if (col?.columnType === 'source') {
      const locationLabel = col?.source?.location?.label || '';
      const locationTypeLabel = col?.source?.locationType?.label || '';
      const attributeLabel = col?.source?.attribute?.label || '';
      const subLocationLabel = col?.source?.subLocation?.label || '';
      const outputLabel = col?.source?.output?.label || '';
      const pom = col?.source?.pom?.label || '';
      const displayFormate = col?.source?.displayFormat?.label || '';

      let result = locationLabel;

      if (locationTypeLabel) {
        result += ` > ${locationTypeLabel}`;
      }

      result += ` > ${attributeLabel} > ${subLocationLabel || outputLabel}`;

      if (subLocationLabel) {
        if (pom) {
          result += ` > ${pom}`;
        }

        if (displayFormate) {
          result += ` > ${displayFormate}`;
        }
      }
      return result;
    }
    return col?.manualType?.type || '';
  };

  return (
    <tr key={id} style={{ ...style, opacity }} data-handler-id={handlerId} ref={ref}>
      {productAddEditAccess && (
        <td>
          <div className="common-checkbox no-label margin-auto">
            <input
              type="checkbox"
              onChange={(e) => handleCheckOdColumns(e.target.checked, id)}
              name={`productCpCheck_row_${id}_${col._id}_${colIdx}`}
              id={`productCpCheck_row_${id}_${col._id}_${colIdx}`}
              checked={selectedColIds?.includes(id)}
              disabled={disableButtons}
            />
            <label htmlFor={`productCpCheck_row_${id}_${col._id}_${colIdx}`} className="chklabel">
              <span />
            </label>
          </div>
        </td>
      )}
      <td className="b-top">{col?.name}</td>
      <td className="b-top">
        {col?.isVisibleToSupplier ? t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.YES') : t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.NO')}
      </td>
      <td className="b-top">
        {Array.isArray(col?.assignee)
          ? [...userGroupsCardList, ...supplierList]
              .filter((colele) => col?.assignee.includes(colele?._id))
              ?.map((e) => e?.name)
              ?.join(', ')
          : [...userGroupsCardList, ...supplierList].find((colele) => col?.assignee === colele?._id)?.name}
      </td>

      <td className="b-top">{col?.columnType === 'manual' ? col?.manualType?.type : renderColumnType()}</td>
      <td className="b-top">
        {col?.isRequired ? t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.REQUIRED') : t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.OPTIONAL')}
      </td>
      {productAddEditAccess && (
        <td>
          <CustomTooltip tooltipText={t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.EDIT')}>
            <button
              type="button"
              aria-label={t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.EDIT')}
              className="border-0 btn-lelete-lib"
              onClick={() => handleEditColumn(col)}
              disabled={disableButtons}
            >
              <span className="icon-Edit-1" />
            </button>
          </CustomTooltip>
          <CustomTooltip
            tooltipText={col?.isActive ? t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.ACTIVE') : t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.INACTIVE')}
          >
            <button
              type="button"
              aria-label={col?.isActive ? t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.ACTIVE') : t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.INACTIVE')}
              disabled={disableButtons}
              className={`border-0 btn-lelete-lib ${col?.isActive ? 'text-success' : 'text-danger'}`}
              onClick={() => handleActiveInactive(col)}
            >
              <i className={col?.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
            </button>
          </CustomTooltip>
          <CustomTooltip tooltipText={t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.MOVE')}>
            <button
              type="button"
              aria-label={t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.MOVE')}
              className="border-0 btn-lelete-lib"
              disabled={disableButtons}
            >
              <span className="icon-Rearrange" />
            </button>
          </CustomTooltip>
          <CustomTooltip tooltipText={t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.DELETE')}>
            <button
              type="button"
              aria-label={t('PRODUCT_CRITICAL_PATH.MOVING_COLUMN_CARDS.DELETE')}
              className="border-0 btn-lelete-lib"
              onClick={() => handleDelete([id])}
              disabled={disableButtons}
            >
              <span className="icon-delete" />
            </button>
          </CustomTooltip>
        </td>
      )}
    </tr>
  );
}

export default MovingColumnCards;
